import { useCallback, useRef } from "react";

/**
 * Razorpay's own payment window, loaded on demand and opened over the page.
 *
 * ## Why this and not our own fields
 *
 * Standard Checkout — `checkout.razorpay.com/v1/checkout.js` — draws the whole
 * payment step itself: which method, the UPI address, the card number, the
 * OTP. All of it is rendered and read inside Razorpay's own frame, from
 * Razorpay's own origin.
 *
 * That is the entire compliance argument. Because no cardholder data is
 * entered into a field this application serves, and none passes through a
 * Naurist server, the obligation stays at the smallest PCI DSS bracket — SAQ A
 * — rather than the SAQ A-EP an in-page card form would carry, or the SAQ D a
 * card reaching our backend would. The acquirer makes that determination, but
 * it is this file that keeps the answer available.
 *
 * Which means: **never add a card field to this application.** Not a "quick
 * one for testing", not behind a flag. It is a one-line change that alters
 * what Naurist is legally obliged to do.
 *
 * The other reason is smaller and still real: Razorpay's window is where new
 * methods appear. Whatever is enabled on the account shows up there without
 * this app shipping anything.
 *
 * ## The script
 *
 * Loaded when the first payment is started, not on page load: it is a
 * third-party script on a route that handles money, and it should not be in
 * the page for the whole time somebody is filling in an address. Loaded once
 * and shared — the promise is kept, so two presses do not fetch it twice.
 *
 * The load can fail. A blocked CDN, an offline moment, a corporate proxy: the
 * caller is handed the failure and says so, rather than a Pay now that does
 * nothing.
 */

const SCRIPT_SRC = "https://checkout.razorpay.com/v1/checkout.js";

/**
 * What Razorpay hands back when a payment goes through.
 *
 * Only the payment id is always there. A subscription's mandate also carries
 * the subscription id and a signature — and note the signature for one is
 * computed over `payment_id + "|" + subscription_id`, the reverse of an
 * order's, which is the backend's problem but the commonest way to get this
 * wrong. A payment taken without either behind it carries neither.
 */
export interface RazorpaySuccess {
  razorpay_payment_id: string;
  razorpay_subscription_id?: string;
  razorpay_order_id?: string;
  razorpay_signature?: string;
}

export interface RazorpayFailure {
  error?: {
    code?: string;
    description?: string;
    reason?: string;
  };
}

/**
 * The options this app passes. Razorpay accepts many more; these are the ones
 * it needs, and adding to them is a deliberate act.
 *
 * Nothing here describes how the window *looks*. The merchant name, the mark in
 * the header, the accent colour and the row of payment methods are all settings
 * on the Razorpay dashboard, and the window reads them from the account it is
 * opened against. Sending any of them from the browser overrides the dashboard
 * silently — so a change made there would appear to do nothing — which is why
 * `name`, `image`, `theme` and `method` are absent rather than optional.
 */
export interface RazorpayOptions {
  /** The *public* key id — see `lib/razorpay`. If a `key_secret` ever reaches
   *  this file, that is an incident. */
  key: string;
  /**
   * The order being paid — Razorpay's own id for it, handed over by our
   * backend, which created it because doing so needs the secret. See
   * `createPrepaidOrder`.
   *
   * **This is the one every window this app opens now carries.** A term is
   * bought outright, which is an order and a single charge; there is no mandate
   * to sign.
   */
  order_id?: string;
  /**
   * The subscription a mandate is being signed for.
   *
   * Nothing here sends it any more — nothing renews, so no mandate is taken.
   * It is kept because the option exists and because the backend still answers
   * for the workspaces that signed one before this.
   */
  subscription_id?: string;
  /**
   * The charge in paise — `70800`, not `708.00`, since Razorpay counts in the
   * smallest unit.
   *
   * **Sent, and sent exactly as the backend gave it.** An order is paid for the
   * amount it was opened for, and the gateway checks this against the order it
   * signed; a figure converted from rupees on this side would be a rounding
   * nobody could account for afterwards. See `PrepaidCheckout.amountInPaise`,
   * which is carried in paise the whole way for this reason.
   */
  amount?: number;
  currency?: string;
  /** What is being bought, in the backend's own words — a line of copy about
   *  this purchase, not a setting the dashboard has an opinion about. */
  description?: string;
  prefill?: { name?: string; email?: string; contact?: string };
  notes?: Record<string, string>;
  handler?: (response: RazorpaySuccess) => void;
  modal?: { ondismiss?: () => void };
}

interface RazorpayInstance {
  open: () => void;
  on: (event: string, handler: (payload: RazorpayFailure) => void) => void;
}

declare global {
  interface Window {
    Razorpay?: new (options: RazorpayOptions) => RazorpayInstance;
  }
}

/** Kept at module scope rather than in the hook: the script belongs to the
 *  document, not to a component, and a remount must not fetch it again. */
let loading: Promise<void> | null = null;

const loadRazorpay = (): Promise<void> => {
  if (window.Razorpay) return Promise.resolve();
  if (loading) return loading;

  loading = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      // Cleared so a later attempt can try again — a failed load is often a
      // moment of bad network rather than a permanent state.
      loading = null;
      reject(new Error("Could not load the payment window."));
    };
    document.body.appendChild(script);
  });

  return loading;
};

export interface OpenCheckoutArgs {
  options: RazorpayOptions;
  /** The mandate was authorised. What comes back still has to be confirmed
   *  against the backend — see `verifySubscription`. */
  onSuccess: (response: RazorpaySuccess) => void;
  /** The gateway reported a failed payment, with its own reason. */
  onFailure: (reason: string) => void;
  /** The window was closed without finishing. Not a failure — nothing was
   *  charged, and the commonest reason is somebody changing their mind. */
  onDismiss: () => void;
}

export interface RazorpayCheckout {
  open: (args: OpenCheckoutArgs) => Promise<void>;
}

/**
 * Opens Razorpay's window for a subscription that the backend has already
 * created.
 *
 * The three callbacks are the three ways it ends, and they are deliberately
 * separate: a dismissed window is not a failed payment, and telling somebody
 * their payment failed when they simply closed the box is how a checkout
 * loses a customer who was about to try again.
 */
export function useRazorpayCheckout(): RazorpayCheckout {
  // Guards against a second window while one is open — a double press on Pay
  // now, or a callback that fires late.
  const openRef = useRef(false);

  const open = useCallback(async (args: OpenCheckoutArgs) => {
    if (openRef.current) return;
    openRef.current = true;

    try {
      await loadRazorpay();
    } catch (cause) {
      openRef.current = false;
      args.onFailure(
        cause instanceof Error
          ? cause.message
          : "Could not load the payment window."
      );
      return;
    }

    if (!window.Razorpay) {
      openRef.current = false;
      args.onFailure("Could not load the payment window.");
      return;
    }

    const instance = new window.Razorpay({
      ...args.options,
      handler: (response) => {
        openRef.current = false;
        args.onSuccess(response);
      },
      modal: {
        ondismiss: () => {
          openRef.current = false;
          args.onDismiss();
        },
      },
    });

    instance.on("payment.failed", (payload) => {
      openRef.current = false;
      args.onFailure(
        payload?.error?.description ??
          "Your bank turned the payment down. Nothing has been charged."
      );
    });

    instance.open();
  }, []);

  return { open };
}
